import chevronLeftIcon from "@loot-os/icons/icons/chevron-left";
import closeIcon from "@loot-os/icons/icons/close";
import settingsIcon from "@loot-os/icons/icons/settings";
import { type ReactNode, useEffect, useRef } from "react";

import { IconButton } from "../../core/IconButton.js";
import { TabItem, TabList, TabPanel, Tabs } from "../../core/Tabs.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { NotificationList } from "./NotificationList.js";
import { NotificationSettings } from "./NotificationSettings.js";
import type { NotificationListContent, NotificationSettingsContent } from "./notificationTypes.js";

export type NotificationPanelView = "list" | "settings";

export interface NotificationPanelProps<T extends string> {
  /** "Notifications". */
  title: string;
  /** Which face shows: the list under its tabs, or the settings behind the gear. */
  view: NotificationPanelView;
  onViewChange: (view: NotificationPanelView) => void;
  /** The tabs over the list (All, Unread); `list` is the selected tab's. */
  tabs: {
    value: T;
    /** The tab list's accessible name. */
    label: string;
    options: readonly { value: T; label: string }[];
    onValueChange: (value: T) => void;
  };
  list: NotificationListContent;
  markAllRead: { label: string; disabled: boolean; onClick: () => void };
  settings: NotificationSettingsContent & {
    /** The settings face's heading: "Notification settings". */
    title: string;
    /** The gear's name. */
    openLabel: string;
    /** The back button's name. */
    backLabel: string;
  };
  /** A close button in the header, for the full-screen panel on a phone. */
  close?: { label: string; onClose: () => void };
  /** Read beside each unread dot. */
  unreadLabel?: string;
}

/**
 * What the notification bell opens: the notifications under their tabs with Mark all as read, or
 * the shopper's settings behind the gear. Fills the height it is given; the list scrolls.
 */
export function NotificationPanel<T extends string>({
  title,
  view,
  onViewChange,
  tabs,
  list,
  markAllRead,
  settings,
  close,
  unreadLabel,
}: NotificationPanelProps<T>) {
  // Switching faces removes the button that was pressed, so focus moves to its counterpart.
  const backRef = useRef<HTMLButtonElement>(null);
  const gearRef = useRef<HTMLButtonElement>(null);
  const switched = useRef(false);
  useEffect(() => {
    if (!switched.current) return;
    (view === "settings" ? backRef : gearRef).current?.focus();
  }, [view]);
  const show = (next: NotificationPanelView) => {
    switched.current = true;
    onViewChange(next);
  };

  const closeButton = close ? (
    <IconButton
      icon={closeIcon}
      label={close.label}
      variant="ghost"
      size="sm"
      onClick={close.onClose}
    />
  ) : null;

  if (view === "settings") {
    const { title: settingsTitle, openLabel: _open, backLabel, ...content } = settings;
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <PanelHeader
          title={settingsTitle}
          leading={
            <IconButton
              ref={backRef}
              icon={chevronLeftIcon}
              label={backLabel}
              variant="ghost"
              size="sm"
              onClick={() => show("list")}
            />
          }
          trailing={closeButton}
        />
        <NotificationSettings {...content} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PanelHeader
        title={title}
        trailing={
          <>
            <IconButton
              ref={gearRef}
              icon={settingsIcon}
              label={settings.openLabel}
              variant="ghost"
              size="sm"
              onClick={() => show("settings")}
            />
            {closeButton}
          </>
        }
      />
      <Tabs
        value={tabs.value}
        onValueChange={(value) => tabs.onValueChange(value as T)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex items-end justify-between gap-2 border-b border-border-subtle px-4">
          <TabList aria-label={tabs.label} className="gap-5 border-b-0">
            {tabs.options.map((option) => (
              <TabItem key={option.value} value={option.value} className="pb-2.5">
                {option.label}
              </TabItem>
            ))}
          </TabList>
          <button
            type="button"
            disabled={markAllRead.disabled}
            onClick={markAllRead.onClick}
            className={cn(
              "mb-2 cursor-pointer rounded-xs font-body text-body-b2-strong text-accent-text hover:text-text-primary disabled:cursor-default disabled:text-text-disabled",
              FOCUS_RING,
            )}
          >
            {markAllRead.label}
          </button>
        </div>
        {tabs.options.map((option) => (
          <TabPanel
            key={option.value}
            value={option.value}
            className="flex min-h-0 flex-1 flex-col data-hidden:hidden"
          >
            {option.value === tabs.value && (
              <NotificationList content={list} unreadLabel={unreadLabel} />
            )}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}

/** The panel's title row, with an optional leading button and actions on the right. */
function PanelHeader({
  title,
  leading,
  trailing,
}: {
  title: string;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
      {leading}
      <h2 className="min-w-0 flex-1 truncate font-body text-heading-h2 text-text-primary">
        {title}
      </h2>
      <div className="flex shrink-0 items-center gap-1">{trailing}</div>
    </div>
  );
}
